/**
 * ACORDE — «¿A dónde te lo enviamos?»: datos de envío validados antes del checkout.
 *
 * Qué hace:
 * 1. Intercepta «Continuar pedido» (el botón `name="checkout"` de la página del carrito y del cajón).
 * 2. Si no hay datos guardados, abre la ventana; si los hay y son válidos, sigue de una.
 * 3. Valida con las reglas de una dirección colombiana (celular de 10 dígitos que empieza por 3,
 *    ciudad de la lista del departamento, dirección con número, barrio obligatorio).
 * 4. Entrega los datos al carrito con la Storefront API (sin token: el carrito de la tienda en línea
 *    se identifica con su propia clave) y abre el checkout, que llega con todo lleno.
 *
 * Nunca bloquea la compra: si la API falla o tarda, el cliente pasa al checkout normal.
 * Los datos se guardan solo en el dispositivo del cliente (localStorage) si marca «Recordar».
 */
const CLAVE = 'acorde_envio';
const ESPERA_MAXIMA = 6000;

const MENSAJES = {
  nombre: 'Escribe tu nombre.',
  apellido: 'Escribe tu apellido.',
  celular: 'El celular debe tener 10 dígitos y empezar por 3.',
  correo: 'Revisa el correo (ejemplo: nombre@correo.com).',
  departamento: 'Elige tu departamento.',
  ciudad: 'Elige tu ciudad o municipio.',
  guiada: 'Completa los tres números de la dirección (ejemplo: Calle 16 # 9-30).',
  libre: 'Escribe la dirección completa, con su número (ejemplo: Manzana 5 Casa 12).',
  barrio: 'Escribe el barrio: la transportadora lo necesita.',
};

/** @returns {Record<string, any> | null} */
function leer() {
  for (const almacen of [window.localStorage, window.sessionStorage]) {
    try {
      const datos = JSON.parse(almacen.getItem(CLAVE) || 'null');
      if (datos && typeof datos === 'object') return datos;
    } catch {
      // Sin almacenamiento o dato corrupto: se pide de nuevo.
    }
  }
  return null;
}

/**
 * @param {Record<string, any>} datos
 * @param {boolean} recordar - true: queda en el dispositivo; false: solo durante esta visita
 */
function guardar(datos, recordar) {
  try {
    window.localStorage.removeItem(CLAVE);
    window.sessionStorage.removeItem(CLAVE);
    (recordar ? window.localStorage : window.sessionStorage).setItem(CLAVE, JSON.stringify(datos));
  } catch {
    // Modo privado: los datos igual viajan al carrito; solo no se recuerdan.
  }
}

/** Deja solo los 10 dígitos del celular (quita espacios, guiones y el indicativo 57). */
function soloCelular(valor) {
  let digitos = String(valor || '').replace(/\D/g, '');
  if (digitos.length === 12 && digitos.startsWith('57')) digitos = digitos.slice(2);
  return digitos;
}

/** «3001234567» → «300 123 4567» */
function celularLegible(valor) {
  const digitos = soloCelular(valor).slice(0, 10);
  return [digitos.slice(0, 3), digitos.slice(3, 6), digitos.slice(6)].filter(Boolean).join(' ');
}

/** «  juan   pablo » → «Juan Pablo» */
function nombrePropio(valor) {
  return String(valor || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/(^|[\s'-])(\p{L})/gu, (_, antes, letra) => antes + letra.toLocaleUpperCase('es'));
}

const limpio = (valor) => String(valor || '').trim().replace(/\s+/g, ' ');

/** Dirección en una línea a partir de los datos del formulario. */
function direccionDe(datos) {
  if (datos.libre) return limpio(datos.direccionLibre);
  if (!datos.n1 && !datos.n2 && !datos.n3) return '';
  return `${datos.via} ${limpio(datos.n1)} # ${limpio(datos.n2)}-${limpio(datos.n3)}`.trim();
}

// ------------------------------------------------------------------ «Mi ACORDE» → esta ventana

const VIAS = ['Avenida Calle', 'Avenida Carrera', 'Calle', 'Carrera', 'Avenida', 'Diagonal', 'Transversal', 'Circular', 'Autopista'];
const sinTildes = (valor) => String(valor || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/** La sesión de «Mi ACORDE», si la entrada propia está encendida y el cliente entró. */
const sesion = () => (window.AcordeSesion?.activa?.() ? window.AcordeSesion : null);

/** «Barrio Los Mayales · Torre 2 apto 301» → { barrio: 'Los Mayales', complemento: 'Torre 2 apto 301' } */
export function partirDireccion2(texto) {
  let barrio = '';
  const resto = [];
  for (const parte of String(texto || '').split('·').map(limpio).filter(Boolean)) {
    const coincide = parte.match(/^barrio\s+(.+)$/i);
    if (coincide && !barrio) barrio = coincide[1];
    else resto.push(parte);
  }
  return { barrio, complemento: resto.join(' · ') };
}

/**
 * Una dirección de «Mi ACORDE» en el formato de esta ventana. «Calle 16 # 9-30» va a la dirección
 * guiada; lo que no tenga ese formato (manzana, vereda…) queda como dirección libre.
 * @param {Record<string, any>} d - dirección como la devuelve la función «cuenta»
 * @param {Record<string, any>} [perfil]
 */
export function desdeCuenta(d, perfil = {}) {
  const linea = limpio(d.direccion1);
  const vias = VIAS.map((via) => via.replace(' ', '\\s+')).join('|');
  const partes = linea.match(new RegExp(`^(${vias})\\s+([\\w ]+?)\\s*(?:#|no\\.?|n°)\\s*(\\w+)\\s*-\\s*(\\w+)$`, 'i'));
  const via = partes ? VIAS.find((v) => sinTildes(v) === sinTildes(partes[1].replace(/\s+/g, ' '))) : '';
  return {
    nombre: d.nombre || perfil.nombre || '',
    apellido: d.apellido || perfil.apellido || '',
    celular: soloCelular(d.celular || perfil.celular),
    correo: perfil.correo || '',
    departamento: d.codigoDepartamento || '',
    ciudad: d.ciudad || '',
    libre: !partes,
    via: via || VIAS[2],
    n1: partes ? partes[2].toUpperCase() : '',
    n2: partes ? partes[3].toUpperCase() : '',
    n3: partes ? partes[4].toUpperCase() : '',
    direccionLibre: partes ? '' : linea,
    ...partirDireccion2(d.direccion2),
  };
}

/** ¿Las dos son la misma entrega? (`completa`: también nombre, celular y complemento) */
function mismaDireccion(a, b, completa = false) {
  const claves = completa ? ['nombre', 'apellido', 'departamento', 'ciudad', 'barrio', 'complemento'] : ['departamento', 'ciudad'];
  return (
    sinTildes(direccionDe(a)) === sinTildes(direccionDe(b)) &&
    claves.every((clave) => sinTildes(a[clave]) === sinTildes(b[clave])) &&
    (!completa || soloCelular(a.celular) === soloCelular(b.celular))
  );
}

/**
 * Reglas de validación. Devuelve un mapa campo → mensaje (vacío si todo está bien).
 * @param {Record<string, any>} datos
 * @param {{ pedirCorreo: boolean }} opciones
 */
export function validar(datos, { pedirCorreo }) {
  /** @type {Record<string, string>} */
  const errores = {};
  const conNumero = (valor) => /\d/.test(valor || '');

  if (limpio(datos.nombre).length < 2) errores.nombre = MENSAJES.nombre;
  if (limpio(datos.apellido).length < 2) errores.apellido = MENSAJES.apellido;
  if (!/^3\d{9}$/.test(soloCelular(datos.celular))) errores.celular = MENSAJES.celular;
  if (pedirCorreo && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(limpio(datos.correo))) errores.correo = MENSAJES.correo;
  if (!datos.departamento) errores.departamento = MENSAJES.departamento;
  if (!limpio(datos.ciudad)) errores.ciudad = MENSAJES.ciudad;

  if (datos.libre) {
    const libre = limpio(datos.direccionLibre);
    if (libre.length < 6 || !conNumero(libre)) errores.direccion = MENSAJES.libre;
  } else if (!conNumero(datos.n1) || !conNumero(datos.n2) || !conNumero(datos.n3)) {
    errores.direccion = MENSAJES.guiada;
  }

  if (limpio(datos.barrio).length < 3) errores.barrio = MENSAJES.barrio;
  return errores;
}

class AcordeDatosEnvio extends HTMLElement {
  /** true mientras se envía al checkout, para no interceptar dos veces */
  #siguiendo = false;
  /** Si al guardar hay que ir al checkout (true) o solo cerrar (false, «Cambiar») */
  #irAlPago = true;
  /** @type {Promise<Record<string, string[]>> | null} */
  #municipios = null;
  /** @type {Record<string, any>[]} */
  #direcciones = [];
  /** @type {Record<string, any>} */
  #perfil = {};
  /** id de la dirección de «Mi ACORDE» elegida, u «otra» */
  #elegida = 'otra';

  connectedCallback() {
    this.dialogo = /** @type {HTMLDialogElement} */ (this.querySelector('dialog'));
    this.formulario = /** @type {HTMLFormElement} */ (this.querySelector('form'));
    if (!this.dialogo || !this.formulario || typeof this.dialogo.showModal !== 'function') return;

    this.pedirCorreo = this.dataset.pedirCorreo === 'true';
    this.campos = this.formulario.elements;

    document.addEventListener('submit', this.#alIrAlPago, true);
    document.addEventListener('click', this.#alTocarCambiar);

    this.formulario.addEventListener('submit', this.#alGuardar);
    this.formulario.addEventListener('input', this.#alEscribir);
    this.campos.departamento.addEventListener('change', () => this.#llenarCiudades(this.campos.departamento.value));
    this.querySelector('[data-cerrar]')?.addEventListener('click', () => this.dialogo.close());
    this.querySelector('[data-cambiar-formato]')?.addEventListener('click', () => this.#formatoLibre(!this.libre));
    this.querySelector('[data-guardadas-lista]')?.addEventListener('click', this.#alElegir);
    // Tocar el fondo oscuro cierra la ventana
    this.dialogo.addEventListener('click', (evento) => {
      if (evento.target === this.dialogo) this.dialogo.close();
    });
    // Escape cierra esta ventana y nada más: si está abierta sobre el cajón del carrito, Horizon
    // escucha la misma tecla en el documento y, sin esto, anula el cierre nativo.
    this.dialogo.addEventListener('keydown', (evento) => {
      if (evento.key !== 'Escape' || evento.isComposing) return;
      evento.preventDefault();
      this.dialogo.close();
    });

    this.pintarResumen();

    // Con sesión, la cuenta se trae en silencio cuando la página ya cargó: al tocar «Continuar»
    // la ventana se llena al instante. (La sesión se define en otro módulo: se espera un momento.)
    // Si en este teléfono no hay datos de envío, la dirección principal de la cuenta los pone
    // (solo para esta visita): así el carrito ya muestra «Enviar a…» sin abrir nada.
    const precargar = async () => {
      const S = sesion();
      if (!S?.resumen) return;
      try {
        const cuenta = await S.resumen();
        if (!leer()) this.#llenarDesdeCuenta(cuenta);
      } catch {
        // Sin cuenta disponible, el carrito sigue igual.
      }
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(precargar, { timeout: 3000 });
    else setTimeout(precargar, 1500);
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.#alIrAlPago, true);
    document.removeEventListener('click', this.#alTocarCambiar);
  }

  get libre() {
    return this.querySelector('[data-libre]')?.hidden === false;
  }

  /** «Continuar pedido»: se intercepta el envío del formulario del carrito. */
  #alIrAlPago = (evento) => {
    if (this.#siguiendo) return;
    const boton = /** @type {HTMLElement | null} */ (evento.submitter);
    if (boton?.getAttribute('name') !== 'checkout') return;

    evento.preventDefault();
    evento.stopPropagation();

    const datos = leer();
    if (datos && Object.keys(validar(datos, { pedirCorreo: this.pedirCorreo })).length === 0) {
      this.#seguir(datos, boton);
    } else {
      this.abrir(true);
    }
  };

  #alTocarCambiar = (evento) => {
    if (!(evento.target instanceof Element) || !evento.target.closest('[data-acorde-envio-cambiar]')) return;
    evento.preventDefault();
    this.abrir(false);
  };

  /** @param {boolean} irAlPago */
  async abrir(irAlPago) {
    this.#irAlPago = irAlPago;
    this.querySelector('[data-texto-pago]').hidden = !irAlPago;
    this.querySelector('[data-texto-guardar]').hidden = irAlPago;
    this.#aviso('');

    const datos = leer();
    if (datos) await this.#rellenar(datos);
    this.#municipiosListos();

    const conSesion = Boolean(sesion());
    this.querySelector('[data-guardar-cuenta]').hidden = !conSesion;
    if (!conSesion) this.querySelector('[data-guardadas]').hidden = true;

    if (!this.dialogo.open) this.dialogo.showModal();
    // Con sesión, la ventana se llena con «Mi ACORDE» mientras ya está abierta (no la frena).
    if (conSesion) this.#usarCuenta(datos);
  }

  /**
   * Trae la cuenta y llena lo que falte: las direcciones guardadas para elegir y, si en este
   * teléfono no hay datos, la dirección principal completa.
   * @param {Record<string, any> | null} local - lo guardado en este dispositivo
   */
  async #usarCuenta(local) {
    let cuenta;
    try {
      const S = sesion();
      cuenta = await (S.resumen ? S.resumen() : S.api('resumen'));
    } catch {
      return;
    }
    const cliente = cuenta?.cliente;
    this.#perfil = { ...(cliente?.perfil || {}), correo: cliente?.perfil?.correo || cuenta?.correo || '' };
    this.#direcciones = cliente?.direcciones || [];

    const c = this.campos;
    if (c.correo && !c.correo.value) c.correo.value = this.#perfil.correo;

    if (!this.#direcciones.length) {
      // Sin direcciones guardadas: al menos el nombre y el celular del perfil.
      if (!c.nombre.value) c.nombre.value = this.#perfil.nombre || '';
      if (!c.apellido.value) c.apellido.value = this.#perfil.apellido || '';
      if (!c.celular.value) c.celular.value = celularLegible(this.#perfil.celular);
      this.#elegida = 'otra';
      return;
    }

    const elegida = local
      ? this.#direcciones.find((d) => mismaDireccion(desdeCuenta(d, this.#perfil), local))
      : this.#direcciones.find((d) => d.principal) || this.#direcciones[0];
    this.#pintarGuardadas(elegida?.id ?? 'otra');
    if (elegida && !local) await this.#rellenar({ ...desdeCuenta(elegida, this.#perfil), correo: c.correo?.value || this.#perfil.correo });
  }

  /** La dirección principal de «Mi ACORDE» como datos de envío de esta visita, si está completa. */
  #llenarDesdeCuenta(cuenta) {
    const cliente = cuenta?.cliente;
    const d = (cliente?.direcciones || []).find((x) => x.principal) || cliente?.direcciones?.[0];
    if (!d) return;
    const perfil = { ...(cliente?.perfil || {}), correo: cliente?.perfil?.correo || cuenta?.correo || '' };
    const datos = desdeCuenta(d, perfil);
    datos.ciudad = nombrePropio(datos.ciudad);
    const opcion = this.querySelector(`select[name="departamento"] option[value="${CSS.escape(datos.departamento)}"]`);
    datos.departamentoNombre = opcion?.textContent?.trim() || d.departamento || '';
    if (Object.keys(validar(datos, { pedirCorreo: this.pedirCorreo })).length) return;
    guardar(datos, false);
    this.pintarResumen();
  }

  /** @param {string} seleccion - id de la dirección elegida, u «otra» */
  #pintarGuardadas(seleccion) {
    const tarjeta = (id, clase, ...hijos) => {
      const boton = document.createElement('button');
      boton.type = 'button';
      boton.className = clase;
      boton.dataset.id = id;
      boton.setAttribute('role', 'radio');
      boton.setAttribute('aria-checked', String(id === seleccion));
      boton.append(...hijos);
      return boton;
    };
    const texto = (etiqueta, contenido) => Object.assign(document.createElement(etiqueta), { textContent: contenido });

    const tarjetas = this.#direcciones.map((d) =>
      tarjeta(
        d.id,
        'acorde-envio__guardada',
        texto('strong', `${d.direccion1}${d.principal ? ' · Principal' : ''}`),
        texto('span', [d.direccion2, [nombrePropio(d.ciudad), d.departamento].filter(Boolean).join(', ')].filter(Boolean).join(' · '))
      )
    );
    this.querySelector('[data-guardadas-lista]').replaceChildren(
      ...tarjetas,
      tarjeta('otra', 'acorde-envio__guardada acorde-envio__guardada--otra', 'Otra dirección')
    );
    this.querySelector('[data-guardadas]').hidden = false;
    this.#elegida = seleccion;
  }

  #alElegir = async (evento) => {
    const boton = evento.target instanceof Element ? evento.target.closest('[data-id]') : null;
    if (!boton) return;
    const id = boton.dataset.id;
    this.#pintarGuardadas(id);
    this.#mostrarErrores({});

    if (id === 'otra') {
      // Se conservan nombre, celular y correo; la dirección queda en blanco para escribir la nueva.
      const { nombre, apellido, celular, correo } = this.#datos();
      await this.#rellenar({ nombre, apellido, celular, correo, departamento: '', ciudad: '', libre: false });
      this.campos.departamento.focus();
      return;
    }
    const d = this.#direcciones.find((x) => x.id === id);
    if (d) await this.#rellenar({ ...desdeCuenta(d, this.#perfil), correo: this.campos.correo?.value || this.#perfil.correo });
  };

  /**
   * Guarda en «Mi ACORDE» lo que se escribió aquí (dirección nueva o corregida) para no volver a
   * pedirlo. Si es una dirección guardada sin cambios, no hace nada. Nunca frena el pago.
   * @param {Record<string, any>} datos
   */
  async #guardarEnCuenta(datos) {
    const S = sesion();
    if (!S || !this.campos.guardar_cuenta?.checked) return;
    const elegida = this.#direcciones.find((d) => d.id === this.#elegida);
    if (elegida && mismaDireccion(desdeCuenta(elegida, this.#perfil), datos, true)) return;

    const cuerpo = {
      id: elegida?.id,
      principal: elegida ? Boolean(elegida.principal) : this.#direcciones.length === 0,
      direccion: {
        nombre: datos.nombre,
        apellido: datos.apellido,
        celular: datos.celular,
        codigoDepartamento: datos.departamento,
        ciudad: datos.ciudad,
        direccion1: direccionDe(datos),
        direccion2: [`Barrio ${datos.barrio}`, datos.complemento].filter(Boolean).join(' · '),
        codigoPais: 'CO',
      },
    };
    try {
      await Promise.race([S.api('direccion_guardar', cuerpo), new Promise((listo) => setTimeout(listo, 3500))]);
    } catch {
      // Si la cuenta no responde, el pedido sigue igual: los datos ya van al checkout.
    }
  }

  /** Carga la lista de municipios la primera vez que hace falta. */
  #municipiosListos() {
    this.#municipios ??= import(this.dataset.municipios).then((modulo) => modulo.default);
    return this.#municipios;
  }

  /** @param {string} codigo - código de departamento */
  async #llenarCiudades(codigo, elegida = '') {
    const ciudad = /** @type {HTMLSelectElement} */ (this.campos.ciudad);
    if (!codigo) {
      ciudad.replaceChildren(new Option('Elige…', ''));
      ciudad.disabled = true;
      return;
    }

    try {
      const municipios = (await this.#municipiosListos())[codigo] || [];
      ciudad.replaceChildren(new Option('Elige…', ''), ...municipios.map((nombre) => new Option(nombre, nombre)));
      // La ciudad que viene de «Mi ACORDE» puede estar escrita sin tilde o en otro orden de mayúsculas.
      const exacta = municipios.find((nombre) => sinTildes(nombre) === sinTildes(elegida));
      if (elegida && !exacta) ciudad.append(new Option(elegida, elegida));
      // Bogotá es su propio departamento y tiene una sola ciudad: se elige sola.
      ciudad.value = municipios.length === 1 ? municipios[0] : exacta || elegida;
      ciudad.disabled = false;
    } catch {
      // Sin la lista (error de red): la ciudad se escribe a mano para no frenar la compra.
      const entrada = document.createElement('input');
      entrada.type = 'text';
      for (const atributo of ['id', 'name', 'autocomplete', 'aria-describedby']) {
        entrada.setAttribute(atributo, ciudad.getAttribute(atributo) || '');
      }
      entrada.required = true;
      entrada.value = elegida;
      ciudad.replaceWith(entrada);
      this.campos = this.formulario.elements;
    }
  }

  /** @param {boolean} libre */
  #formatoLibre(libre) {
    this.querySelector('[data-guiada]').hidden = libre;
    this.querySelector('[data-libre]').hidden = !libre;
    this.querySelector('[data-texto-guiada]').hidden = libre;
    this.querySelector('[data-texto-libre]').hidden = !libre;
    this.querySelector('[data-cambiar-formato]').setAttribute('aria-pressed', String(libre));
    this.#vistaPrevia();
  }

  /** Lee el formulario y deja los datos normalizados. */
  #datos() {
    const c = this.campos;
    return {
      nombre: nombrePropio(c.nombre.value),
      apellido: nombrePropio(c.apellido.value),
      celular: soloCelular(c.celular.value),
      correo: c.correo ? limpio(c.correo.value).toLowerCase() : '',
      departamento: c.departamento.value,
      departamentoNombre: c.departamento.selectedOptions[0]?.textContent?.trim() || '',
      ciudad: limpio(c.ciudad.value),
      libre: this.libre,
      via: c.via.value,
      n1: limpio(c.n1.value).toUpperCase(),
      n2: limpio(c.n2.value).toUpperCase(),
      n3: limpio(c.n3.value).toUpperCase(),
      direccionLibre: limpio(c.direccion_libre.value),
      barrio: nombrePropio(c.barrio.value),
      complemento: limpio(c.complemento.value),
    };
  }

  /** @param {Record<string, any>} datos */
  async #rellenar(datos) {
    const c = this.campos;
    c.nombre.value = datos.nombre || '';
    c.apellido.value = datos.apellido || '';
    c.celular.value = celularLegible(datos.celular);
    if (c.correo) c.correo.value = datos.correo || '';
    c.departamento.value = datos.departamento || '';
    await this.#llenarCiudades(c.departamento.value, datos.ciudad || '');
    if (datos.via) c.via.value = datos.via;
    c.n1.value = datos.n1 || '';
    c.n2.value = datos.n2 || '';
    c.n3.value = datos.n3 || '';
    c.direccion_libre.value = datos.direccionLibre || '';
    c.barrio.value = datos.barrio || '';
    c.complemento.value = datos.complemento || '';
    this.#formatoLibre(Boolean(datos.libre));
  }

  #vistaPrevia() {
    const vista = this.querySelector('[data-vista]');
    const direccion = direccionDe(this.#datos());
    vista.textContent = !this.libre && direccion ? `Así queda: ${direccion}` : '';
  }

  /** Al corregir un campo se le quita el error; la dirección se va mostrando armada. */
  #alEscribir = (evento) => {
    const campo = evento.target;
    if (campo instanceof HTMLElement && campo.getAttribute('aria-invalid') === 'true') {
      campo.removeAttribute('aria-invalid');
    }
    if (campo instanceof HTMLInputElement && campo.name === 'celular') {
      // Solo números, agrupados «300 123 4567» para que se lea bien
      campo.value = celularLegible(campo.value);
    }
    this.#vistaPrevia();
  };

  /** @param {Record<string, string>} errores */
  #mostrarErrores(errores) {
    const destinos = {
      nombre: ['nombre'],
      apellido: ['apellido'],
      celular: ['celular'],
      correo: ['correo'],
      departamento: ['departamento'],
      ciudad: ['ciudad'],
      direccion: this.libre ? ['direccion_libre'] : ['n1', 'n2', 'n3'],
      barrio: ['barrio'],
    };
    let primero = null;

    for (const [clave, nombres] of Object.entries(destinos)) {
      const mensaje = errores[clave] || '';
      const parrafo = this.querySelector(`#acorde-envio-${clave}-error`);
      if (parrafo) {
        parrafo.textContent = mensaje;
        parrafo.hidden = !mensaje;
      }
      for (const nombre of nombres) {
        const campo = this.campos[nombre];
        if (!campo) continue;
        if (mensaje) {
          campo.setAttribute('aria-invalid', 'true');
          primero ??= campo;
        } else {
          campo.removeAttribute('aria-invalid');
        }
      }
    }

    primero?.focus();
  }

  /** @param {string} texto */
  #aviso(texto) {
    const aviso = this.querySelector('[data-aviso]');
    aviso.textContent = texto;
    aviso.hidden = !texto;
  }

  #alGuardar = async (evento) => {
    evento.preventDefault();
    const datos = this.#datos();
    const errores = validar(datos, { pedirCorreo: this.pedirCorreo });
    this.#mostrarErrores(errores);
    if (Object.keys(errores).length > 0) return;

    guardar(datos, this.campos.recordar.checked);
    this.pintarResumen();

    const boton = /** @type {HTMLElement} */ (this.querySelector('[data-enviar]'));
    // La cuenta se guarda al mismo tiempo que se prepara el checkout: ninguna espera a la otra.
    const enCuenta = this.#guardarEnCuenta(datos);
    if (this.#irAlPago) {
      await this.#seguir(datos, boton, enCuenta);
    } else {
      boton.setAttribute('aria-busy', 'true');
      await Promise.all([this.#entregarAlCarrito(datos), enCuenta]);
      boton.removeAttribute('aria-busy');
      this.dialogo.close();
    }
  };

  /**
   * Entrega los datos al carrito y abre el checkout.
   * @param {Record<string, any>} datos
   * @param {HTMLElement | null} boton
   * @param {Promise<unknown>} [enCuenta] - el guardado en «Mi ACORDE», si se está haciendo
   */
  async #seguir(datos, boton, enCuenta) {
    this.#siguiendo = true;
    boton?.setAttribute('aria-busy', 'true');
    await Promise.all([this.#entregarAlCarrito(datos), enCuenta]);
    window.location.assign(this.dataset.pago);
  }

  /**
   * Storefront API: contacto + dirección de entrega del carrito actual.
   * Devuelve true si Shopify aceptó los datos; false si hubo cualquier problema (se sigue igual).
   * @param {Record<string, any>} datos
   */
  async #entregarAlCarrito(datos) {
    const control = new AbortController();
    const reloj = setTimeout(() => control.abort(), ESPERA_MAXIMA);

    try {
      const carrito = await (await fetch(this.dataset.carrito, { signal: control.signal })).json();
      // El token del carrito trae su clave («…?key=…»); sin ella la API no deja modificarlo.
      if (!carrito?.token || !String(carrito.token).includes('?key=')) return false;

      const celular = `+57${soloCelular(datos.celular)}`;
      const direccion2 = [`Barrio ${datos.barrio}`, datos.complemento].filter(Boolean).join(' · ');
      const consulta = `mutation AcordeDatosEnvio($cartId: ID!, $identidad: CartBuyerIdentityInput!, $direcciones: [CartSelectableAddressInput!]!) {
        cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $identidad) { userErrors { field message } }
        cartDeliveryAddressesReplace(cartId: $cartId, addresses: $direcciones) { userErrors { field message } }
      }`;
      const variables = {
        cartId: `gid://shopify/Cart/${carrito.token}`,
        identidad: { countryCode: 'CO', phone: celular, ...(datos.correo ? { email: datos.correo } : {}) },
        direcciones: [
          {
            selected: true,
            oneTimeUse: true,
            address: {
              deliveryAddress: {
                firstName: datos.nombre,
                lastName: datos.apellido,
                address1: direccionDe(datos),
                address2: direccion2,
                city: datos.ciudad,
                provinceCode: datos.departamento,
                countryCode: 'CO',
                phone: celular,
              },
            },
          },
        ],
      };

      const respuesta = await fetch(this.dataset.api, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: consulta, variables }),
        signal: control.signal,
      });
      const resultado = await respuesta.json();
      const fallos = [
        ...(resultado.errors || []),
        ...(resultado.data?.cartBuyerIdentityUpdate?.userErrors || []),
        ...(resultado.data?.cartDeliveryAddressesReplace?.userErrors || []),
      ];
      return respuesta.ok && fallos.length === 0;
    } catch {
      return false;
    } finally {
      clearTimeout(reloj);
    }
  }

  /** Actualiza las cajas «Enviar a…» del carrito (página y cajón). */
  pintarResumen() {
    for (const resumen of document.querySelectorAll('acorde-envio-resumen')) {
      if (resumen instanceof AcordeEnvioResumen) resumen.pintar();
    }
  }
}

/**
 * «Enviar a…» en el resumen del carrito. Horizon vuelve a pintar el resumen cada vez que cambia el
 * carrito (y lo deja vacío, como viene del servidor), así que la caja se observa a sí misma y se
 * vuelve a llenar con los datos guardados.
 */
class AcordeEnvioResumen extends HTMLElement {
  connectedCallback() {
    this.observador = new MutationObserver(() => this.pintar());
    this.observador.observe(this, { attributes: true, attributeFilter: ['hidden'], childList: true, subtree: true });
    this.pintar();
  }

  disconnectedCallback() {
    this.observador?.disconnect();
  }

  pintar() {
    const texto = this.querySelector('[data-acorde-envio-texto]');
    const pedirCorreo = document.querySelector('acorde-datos-envio')?.dataset.pedirCorreo === 'true';
    const datos = leer();
    const completo = Boolean(datos) && Object.keys(validar(datos, { pedirCorreo })).length === 0;
    if (!texto) return;

    const linea1 = completo ? `${direccionDe(datos)}, Barrio ${datos.barrio}` : '';
    // «Bogotá, Bogotá D.C.» sobra: si el departamento ya nombra la ciudad, basta con él.
    const lugar = completo && datos.departamentoNombre.startsWith(datos.ciudad) ? datos.departamentoNombre : `${datos?.ciudad}, ${datos?.departamentoNombre}`;
    const linea2 = completo ? `${lugar} · ${datos.nombre} ${datos.apellido}` : '';
    const firma = `${linea1}|${linea2}`;

    // Solo se toca el DOM si hace falta: el observador volvería a llamar a pintar() sin fin.
    if (this.hidden !== !completo) this.hidden = !completo;
    if (texto.dataset.firma !== firma || texto.childNodes.length === 0) {
      const destacado = document.createElement('strong');
      destacado.textContent = linea1;
      texto.replaceChildren(destacado, linea2);
      texto.dataset.firma = firma;
    }
  }
}

if (!customElements.get('acorde-envio-resumen')) {
  customElements.define('acorde-envio-resumen', AcordeEnvioResumen);
}

if (!customElements.get('acorde-datos-envio')) {
  customElements.define('acorde-datos-envio', AcordeDatosEnvio);
}
